import React, { useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { InstagramCreatorAgeBucketInput, InstagramCreatorGender, InstagramCreatorCountry, InstagramCreatorInterest } from '@frontend/gql/social/graphql';
import { QueryVariables } from '../types';

interface UseCreatorSearchParamsProps {
  defaultVariables?: QueryVariables;
}

export const useCreatorSearchParams = ({ defaultVariables }: UseCreatorSearchParamsProps) => {
  const location = useLocation();
  const history = useHistory();

  // Parse search params from URL
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const searchQuery = searchParams.get('query') || '';

  // Update URL search params
  const updateUrlParams = useCallback((params: Partial<QueryVariables>) => {
    const urlParams = new URLSearchParams(location.search);

    // Handle query parameter
    if (params.query !== undefined) {
      if (params.query) {
        urlParams.set('query', params.query);
      } else {
        urlParams.delete('query');
      }
    }

    if (params.before !== undefined) {
      if (params.before) {
        urlParams.set('before', params.before);
      } else {
        urlParams.delete('before');
      }
    }

    if (params.after !== undefined) {
      if (params.after) {
        urlParams.set('after', params.after);
      } else {
        urlParams.delete('after');
      }
    }

    // Handle array parameters
    if (params.ageBuckets !== undefined) {
      if (params.ageBuckets.length > 0) {
        urlParams.set('ageBuckets', params.ageBuckets.join(','));
      } else {
        urlParams.delete('ageBuckets');
      }
    }

    if (params.genders !== undefined) {
      if (params.genders.length > 0 && params.genders[0] !== 'ALL') {
        urlParams.set('genders', params.genders.join(','));
      } else {
        urlParams.delete('genders');
      }
    }

    if (params.countries !== undefined) {
      if (params.countries.length > 0) {
        urlParams.set('countries', params.countries.join(','));
      } else {
        urlParams.delete('countries');
      }
    }

    if (params.interests !== undefined) {
      if (params.interests.length > 0) {
        urlParams.set('interests', params.interests.join(','));
      } else {
        urlParams.delete('interests');
      }
    }

    // Handle minFollowers parameter
    if (params.minFollowers !== undefined) {
      if (params.minFollowers !== null && params.minFollowers !== 0) {
        urlParams.set('minFollowers', params.minFollowers.toString());
      } else {
        urlParams.delete('minFollowers');
      }
    }

    // Handle maxFollowers parameter
    if (params.maxFollowers !== undefined) {
      if (params.maxFollowers !== null && params.maxFollowers !== 0) {
        urlParams.set('maxFollowers', params.maxFollowers.toString());
      } else {
        urlParams.delete('maxFollowers');
      }
    }

    // Handle minEngagements parameter
    if (params.minEngagements !== undefined) {
      if (params.minEngagements !== null && params.minEngagements !== 0) {
        urlParams.set('minEngagements', params.minEngagements.toString());
      } else {
        urlParams.delete('minEngagements');
      }
    }

    // Handle maxEngagements parameter
    if (params.maxEngagements !== undefined) {
      if (params.maxEngagements !== null && params.maxEngagements !== 0) {
        urlParams.set('maxEngagements', params.maxEngagements.toString());
      } else {
        urlParams.delete('maxEngagements');
      }
    }

    // Handle majorAudienceAgeBuckets parameter
    if (params.majorAudienceAgeBuckets !== undefined) {
      if (params.majorAudienceAgeBuckets.length > 0) {
        urlParams.set('majorAudienceAgeBuckets', params.majorAudienceAgeBuckets.join(','));
      } else {
        urlParams.delete('majorAudienceAgeBuckets');
      }
    }

    // Handle majorAudienceGenders parameter
    if (params.majorAudienceGenders !== undefined) {
      if (params.majorAudienceGenders.length > 0 && params.majorAudienceGenders[0] !== 'ALL') {
        urlParams.set('majorAudienceGenders', params.majorAudienceGenders.join(','));
      } else {
        urlParams.delete('majorAudienceGenders');
      }
    }

    // Handle majorAudienceCountries parameter
    if (params.majorAudienceCountries !== undefined) {
      if (params.majorAudienceCountries.length > 0) {
        urlParams.set('majorAudienceCountries', params.majorAudienceCountries.join(','));
      } else {
        urlParams.delete('majorAudienceCountries');
      }
    }

    // Handle any additional parameters from QueryVariables
    Object.entries(params).forEach(([key, value]) => {
      if (!['query', 'before', 'after', 'limit', 'ageBuckets', 'genders', 'countries', 'interests', 'minFollowers', 'maxFollowers', 'minEngagements', 'maxEngagements', 'majorAudienceAgeBuckets', 'majorAudienceGenders', 'majorAudienceCountries'].includes(key) && value !== undefined) {
        if (value === null || value === '') {
          urlParams.delete(key);
        } else {
          urlParams.set(key, value.toString());
        }
      }
    });

    history.replace({ pathname: location.pathname, search: urlParams.toString() });
  }, [history, location.pathname, location.search]);

  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateUrlParams({
      ...defaultVariables,
      query: e.target.value
    });
  }, [updateUrlParams, defaultVariables]);

  // Clear search input
  const handleClearSearch = useCallback(() => {
    updateUrlParams({
      ...defaultVariables,
      query: '',
    });
  }, [updateUrlParams, defaultVariables]);

  // Update the search term in the URL
  const setSearchQuery = useCallback((value: string) => {
    updateUrlParams({
      ...defaultVariables,
      query: value,
    });
  }, [updateUrlParams, defaultVariables]);

  // Calculate current URL variables with defaults
  const urlVariables = useMemo<QueryVariables>(() => {
    const ageBuckets = (searchParams.get('ageBuckets')?.split(',').filter(Boolean) || defaultVariables?.ageBuckets || []) as InstagramCreatorAgeBucketInput[];
    const genders = (searchParams.get('genders')?.split(',').filter(Boolean) || defaultVariables?.genders || []) as (InstagramCreatorGender | 'ALL')[];
    const countries = (searchParams.get('countries')?.split(',').filter(Boolean) || defaultVariables?.countries || []) as InstagramCreatorCountry[];
    const interests = (searchParams.get('interests')?.split(',').filter(Boolean) || defaultVariables?.interests || []) as InstagramCreatorInterest[];
    const minFollowers = searchParams.get('minFollowers')
      ? parseInt(searchParams.get('minFollowers') || '', 10)
      : defaultVariables?.minFollowers;
    const maxFollowers = searchParams.get('maxFollowers')
      ? parseInt(searchParams.get('maxFollowers') || '', 10)
      : defaultVariables?.maxFollowers;
    const minEngagements = searchParams.get('minEngagements')
      ? parseInt(searchParams.get('minEngagements') || '', 10)
      : defaultVariables?.minEngagements;
    const maxEngagements = searchParams.get('maxEngagements')
      ? parseInt(searchParams.get('maxEngagements') || '', 10)
      : defaultVariables?.maxEngagements;
    const majorAudienceAgeBuckets = (searchParams.get('majorAudienceAgeBuckets')?.split(',').filter(Boolean) || defaultVariables?.majorAudienceAgeBuckets || []) as InstagramCreatorAgeBucketInput[];
    const majorAudienceGenders = (searchParams.get('majorAudienceGenders')?.split(',').filter(Boolean) || defaultVariables?.majorAudienceGenders || ['ALL']) as (InstagramCreatorGender | 'ALL')[];
    const majorAudienceCountries = (searchParams.get('majorAudienceCountries')?.split(',').filter(Boolean) || defaultVariables?.majorAudienceCountries || []) as InstagramCreatorCountry[];
    const before = searchParams.get('before') || defaultVariables?.before || null;
    const after = searchParams.get('after') || defaultVariables?.after || null;

    return {
      query: searchQuery,
      // limit,
      ageBuckets,
      genders,
      countries,
      interests,
      minFollowers,
      maxFollowers,
      minEngagements,
      maxEngagements,
      majorAudienceAgeBuckets,
      majorAudienceGenders,
      majorAudienceCountries,
      before,
      after,
    };
  }, [searchParams, searchQuery, defaultVariables]);

  const queryVariables = useMemo<QueryVariables>(() => {
    return {
      ...urlVariables,
      // Transform for backend query if needed
      limit: urlVariables.limit || defaultVariables?.limit,
    };
  }, [urlVariables, defaultVariables?.limit]);

  return {
    queryVariables,
    searchQuery,
    setSearchQuery,
    handleSearchChange,
    handleClearSearch,
    urlVariables,
    updateUrlParams
  };
};
